import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useQuery } from '@apollo/client';

import InboxHomeScreenIcon from '../../common/components/icons/InboxHomeScreenIcon';
import MyTasksHomeScreenIcon from '../../common/components/icons/MyTasksHomeScreenIcon';
import OverviewHomeScreenIcon from '../../common/components/icons/OverviewHomeScreenIcon';
import ReportHomeScreenIcon from '../../common/components/icons/ReportHomeScreenIcon';
import MenuLink from '../../common/components/MenuLink';

import {
    inboxPageRoles,
    ncrOverviewPageRoles,
    reportPageRoles,
    tasksPageRoles,
    ticketsKanbanBoardPageRoles,
} from '../../common/constants/userRoles';
import { useUserSettingsContext } from '../../common/providers/userSettings/store/userSettings.context';

import { useCurrentUserBySub } from '../../utils/awsAuth';
import { GET_PAGINATED_NCRS } from '../inbox/operations/queries/getPaginatedNCRs';
import { SEARCH_PAGINATED_WORKFLOW_STEPS_BY_NCR } from '../my-tasks/operations/queries/searchPaginatedWorkflowStepsByNCR';
import { GET_PAGINATED_CAPA_TICKET } from '../../common/gqlQueries/capaTickets';
import { ncrStatus } from '../../common/enums/ncrStatus';
import { stepStatus } from '../../common/enums/stepStatus';

const SectionCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2, 2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3),
    },
    borderRadius: theme.spacing(2),
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(3),
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '13px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '14px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '15px',
    },
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
}));

const CaptureButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #C2185B 0%, #E91E63 100%)',
    color: '#FFFFFF',
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '13px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '14px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '15px',
    },
    boxShadow: '0px 4px 12px rgba(233, 30, 99, 0.3)',
    '&:hover': {
        background: 'linear-gradient(135deg, #AD1457 0%, #C2185B 100%)',
        boxShadow: '0px 6px 16px rgba(233, 30, 99, 0.4)',
    },
}));

const OverviewCard = styled(Card)(({ theme, gradientcolor }) => ({
    height: '100%',
    borderRadius: theme.spacing(2),
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    transition: 'box-shadow 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    },
}));

const GradientIconSection = styled(Box)(({ theme, gradientcolor }) => {
    const gradients = {
        blue: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
        purple: 'linear-gradient(135deg, #5B21B6 0%, #8B5CF6 100%)',
        magenta: 'linear-gradient(135deg, #86198F 0%, #C026D3 100%)',
        green: 'linear-gradient(135deg, #15803D 0%, #22C55E 100%)',
    };
    
    return {
        background: gradients[gradientcolor] || gradients.blue,
        padding: theme.spacing(3),
        display: 'none',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100px',
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
            padding: theme.spacing(4),
            minHeight: '120px',
        },
    };
});

const CardContentSection = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2.5),
    },
    backgroundColor: theme.palette.background.paper,
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
    fontSize: '10px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '11px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '11px',
    },
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    fontSize: '15px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '17px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '18px',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '19px',
    },
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(1.5),
    },
}));

const CardDescription = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '13px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '13.5px',
    },
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    hyphens: 'auto',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

const StatusBadge = styled(Box)(({ theme, variant }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.25),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0.5, 1.5),
    },
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(0.6, 1.75),
    },
    borderRadius: theme.spacing(1),
    fontSize: '10.5px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '11.5px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '12px',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '12.5px',
    },
    fontWeight: 600,
    backgroundColor: variant === 'critical' 
        ? 'rgba(239, 68, 68, 0.1)' 
        : 'rgba(59, 130, 246, 0.1)',
    color: variant === 'critical' 
        ? '#DC2626' 
        : '#2563EB',
    marginRight: theme.spacing(0.75),
    [theme.breakpoints.up('sm')]: {
        marginRight: theme.spacing(1),
    },
    marginBottom: theme.spacing(0.5),
}));

const HomeScreen = () => {
    const { t } = useTranslation();

    const { data } = useCurrentUserBySub();

    const { userData } = useUserSettingsContext();

    const isAllowedRoport = userData && userData.roles?.some(userRole => reportPageRoles?.some(role => role === userRole));
    const isAllowedCapaTickets = userData && userData.roles?.some(userRole => ticketsKanbanBoardPageRoles?.some(role => role === userRole));

    // Fetch inbox NCRs (open requests)
    const { data: inboxData } = useQuery(GET_PAGINATED_NCRS, {
        variables: {
            limit: 1,
            filter: JSON.stringify({ status: ncrStatus.OPEN, archived: false, deleted: false }),
        },
        skip: !data?.roles?.some(userRole => inboxPageRoles?.some(role => role === userRole)),
    });

    // Fetch critical NCRs in inbox
    const { data: criticalInboxData } = useQuery(GET_PAGINATED_NCRS, {
        variables: {
            limit: 1,
            filter: JSON.stringify({ status: ncrStatus.OPEN, archived: false, deleted: false, priority: 'critical' }),
        },
        skip: !data?.roles?.some(userRole => inboxPageRoles?.some(role => role === userRole)),
    });

    // Fetch tasks assigned to current user
    const { data: tasksData } = useQuery(SEARCH_PAGINATED_WORKFLOW_STEPS_BY_NCR, {
        variables: {
            limit: 1,
            filter: JSON.stringify({
                'responsible.responsibleID': data?.sub,
                deleted: false,
                status: ncrStatus.IN_WORK,
            }),
        },
        skip: !data?.sub || !data?.roles?.some(userRole => tasksPageRoles?.some(role => role === userRole)),
    });

    // Fetch active NCRs (in work)
    const { data: activeNCRsData } = useQuery(GET_PAGINATED_NCRS, {
        variables: {
            limit: 1,
            filter: JSON.stringify({ status: ncrStatus.IN_WORK, archived: false, deleted: false }),
        },
        skip: !data?.roles?.some(userRole => ncrOverviewPageRoles?.some(role => role === userRole)),
    });

    // Fetch finalized NCRs (done)
    const { data: finalizedNCRsData } = useQuery(GET_PAGINATED_NCRS, {
        variables: {
            limit: 1,
            filter: JSON.stringify({ status: ncrStatus.DONE, archived: false, deleted: false }),
        },
        skip: !data?.roles?.some(userRole => ncrOverviewPageRoles?.some(role => role === userRole)),
    });

    // Fetch active tickets (in work or open)
    const { data: activeTicketsData } = useQuery(GET_PAGINATED_CAPA_TICKET, {
        variables: {
            limit: 1,
            filter: JSON.stringify({
                $and: [
                    { deleted: false },
                    { 
                        $or: [
                            { status: stepStatus.IN_WORK },
                            { status: stepStatus.OPEN },
                            { status: stepStatus.WAITING },
                            { status: stepStatus.DELAYED }
                        ]
                    }
                ]
            }),
        },
        skip: !isAllowedCapaTickets,
    });

    // Fetch finished tickets (done)
    const { data: finishedTicketsData } = useQuery(GET_PAGINATED_CAPA_TICKET, {
        variables: {
            limit: 1,
            filter: JSON.stringify({ status: stepStatus.DONE, deleted: false }),
        },
        skip: !isAllowedCapaTickets,
    });

    // Extract counts from the data
    const inboxCount = inboxData?.getPaginatedNCRs?.metadata?.total || 0;
    const criticalCount = criticalInboxData?.getPaginatedNCRs?.metadata?.total || 0;
    const tasksCount = tasksData?.searchPaginatedWorkflowStepsByNCR?.metadata?.total || 0;
    const activeNCRsCount = activeNCRsData?.getPaginatedNCRs?.metadata?.total || 0;
    const finalizedNCRsCount = finalizedNCRsData?.getPaginatedNCRs?.metadata?.total || 0;
    const activeTicketsCount = activeTicketsData?.getPaginatedCapaTickets?.metadata?.total || 0;
    const finishedTicketsCount = finishedTicketsData?.getPaginatedCapaTickets?.metadata?.total || 0;

    return (
        <Container maxWidth="md" sx={{ padding: { xs: '1em', sm: '1.5em', md: '2em' } }}>
            {/* Quick Actions Section */}
            {data?.roles?.some(userRole => reportPageRoles?.some(role => role === userRole)) && (
                <SectionCard>
                    <SectionTitle>Quick Actions</SectionTitle>
                    <MenuLink to="/report">
                        <CaptureButton 
                            startIcon={<FiberManualRecordIcon />}
                            fullWidth
                            sx={{ 
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { sm: '200px' }
                            }}
                        >
                            Vorfall erfassen
                        </CaptureButton>
                    </MenuLink>
                </SectionCard>
            )}

            {/* Overview Section */}
            <SectionCard>
                <SectionTitle>Overview</SectionTitle>
                <Grid container spacing={2}>
                    {/* Inbox Card */}
                    {data?.roles?.some(userRole => inboxPageRoles?.some(role => role === userRole)) && (
                        <Grid item xs={12} sm={6} md={6} lg={4}>
                            <MenuLink to="/inbox">
                                <OverviewCard gradientcolor="blue">
                                    <GradientIconSection gradientcolor="blue">
                                        <InboxHomeScreenIcon />
                                    </GradientIconSection>
                                    <CardContentSection>
                                        <CategoryLabel>
                                            {t('home-page.newMessages.inbox')}
                                        </CategoryLabel>
                                        <CardTitle>Neue Meldungen</CardTitle>

                                        <Box display="flex" flexWrap="wrap">
                                            <StatusBadge>Offene Anfragen: {inboxCount}</StatusBadge>
                                            <StatusBadge variant="critical">Kritsch: {criticalCount}</StatusBadge>
                                        </Box>
                                    </CardContentSection>
                                </OverviewCard>
                            </MenuLink>
                        </Grid>
                    )}

                    {/* Tasks Card */}
                    {data?.roles?.some(userRole => tasksPageRoles?.some(role => role === userRole)) && (
                        <Grid item xs={12} sm={6} md={6} lg={4}>
                            <MenuLink to="/my-tasks">
                                <OverviewCard gradientcolor="purple">
                                    <GradientIconSection gradientcolor="purple">
                                        <OverviewHomeScreenIcon />
                                    </GradientIconSection>
                                    <CardContentSection>
                                        <CategoryLabel>
                                            {t('home-page.myTasks.overview')}
                                        </CategoryLabel>
                                        <CardTitle>Aufgaben</CardTitle>

                                        <Box display="flex" flexWrap="wrap">
                                            <StatusBadge>Mir zugewiesen: {tasksCount}</StatusBadge>
                                        </Box>
                                    </CardContentSection>
                                </OverviewCard>
                            </MenuLink>
                        </Grid>
                    )}

                    {/* NCR Overview Card */}
                    {data?.roles?.some(userRole => ncrOverviewPageRoles?.some(role => role === userRole)) && (
                        <Grid item xs={12} sm={6} md={6} lg={4}>
                            <MenuLink to="/ncr-overview">
                                <OverviewCard gradientcolor="magenta">
                                    <GradientIconSection gradientcolor="magenta">
                                        <MyTasksHomeScreenIcon />
                                    </GradientIconSection>
                                    <CardContentSection>
                                        <CategoryLabel>
                                            {t('home-page.ncrOverview.overview')}
                                        </CategoryLabel>
                                        <CardTitle>NCR</CardTitle>

                                        <Box display="flex" flexWrap="wrap">
                                            <StatusBadge>Aktiv: {activeNCRsCount}</StatusBadge>
                                            <StatusBadge variant="critical">Abgeschlossen: {finalizedNCRsCount}</StatusBadge>
                                        </Box>
                                    </CardContentSection>
                                </OverviewCard>
                            </MenuLink>
                        </Grid>
                    )}

                    {/* Tickets Card */}
                    {isAllowedCapaTickets && (
                        <Grid item xs={12} sm={6} md={6} lg={4}>
                            <MenuLink to="/tickets-kanban-board">
                                <OverviewCard gradientcolor="green">
                                    <GradientIconSection gradientcolor="green">
                                        <ReportHomeScreenIcon />
                                    </GradientIconSection>
                                    <CardContentSection>
                                        <CategoryLabel>
                                            Сorrective & preventive measures
                                        </CategoryLabel>
                                        <CardTitle>Tickets</CardTitle>

                                        <Box display="flex" flexWrap="wrap">
                                            <StatusBadge>Aktiv: {activeTicketsCount}</StatusBadge>
                                            <StatusBadge variant="critical">Abgeschlossen: {finishedTicketsCount}</StatusBadge>
                                        </Box>
                                    </CardContentSection>
                                </OverviewCard>
                            </MenuLink>
                        </Grid>
                    )}
                </Grid>
            </SectionCard>
        </Container>
    );
};

export default HomeScreen;
